import { useEffect, useLayoutEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReadingProgress from "@/components/ReadingProgress";
import { MotionConfig } from "framer-motion";
import { usePerspective } from "@/context/PerspectiveContext";

const ROUTE_PERSPECTIVE = [
  [/^\/(gemeenten|voor-gemeenten|voor-opdrachtgevers|pilot|programmabouwer|onderzoek-en-onderbouwing)/, "opdrachtgever"],
  [/^\/(voor-jou|deelnemers|vonkvinder|probeer|verhalen)/, "deelnemer"],
];

export default function Layout() {
  const location = useLocation();
  const { perspective, setPerspective } = usePerspective();

  useLayoutEffect(() => {
    const match = ROUTE_PERSPECTIVE.find(([re]) => re.test(location.pathname));
    if (match && match[1] !== perspective) setPerspective(match[1]);
  }, [location.pathname, perspective, setPerspective]);

  // Lenis smooth scroll, momentum but not overly slick
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
    });
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    const id = requestAnimationFrame(raf);
    // expose so other components can scroll via lenis if needed
    window.__lenis = lenis;
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // simple reveal-on-scroll for elements with .reveal
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [location.pathname]);

  return (
    <MotionConfig reducedMotion="user">
    <div className="min-h-screen bg-ll-cream text-ll-ink overflow-x-hidden">
      <ReadingProgress />
      <Header />
      <main className="pt-20">
        <Outlet />
      </main>
      <Footer />
    </div>
    </MotionConfig>
  );
}
